/**
 * @lines98/shared/config/seo — SEO / i18n / branding constants (ADR-0020).
 *
 * ЗАЧЕМ / WHY THIS EXISTS: контент-страницы (мини-SSG, ADR-0020 Решение 1) и
 * шаринг-страницы реплеев (T-10) обязаны строить `<title>`, OG-мета, schema.org,
 * canonical, hreflang и язык-каркас из ЕДИНОГО источника, а не хардкодить бренд /
 * домен / язык по тексту шаблонов. Это та же дисциплина «числа в config», только
 * для строк-идентичности продукта (CLAUDE.md hard-rule; code-structure §4 «текст
 * = данные»). Owner-inputs 2026-06-16 (`docs/research/seo/owner-inputs.md`) задал
 * домен/бренд/дефолт-язык как авторитетные значения — они живут здесь.
 *
 * SCOPE: брендинг (имя/домен/canonical base), i18n-каркас (ДВА списка языков —
 *   `UI_LANGS` 12 ⊇ `CONTENT_LANGS` индексируемый; подписи-эндонимы / коды-флаги /
 *   направление письма / hreflang-теги / country→lang маппинг), путь к логотипу.
 *   Импортируется build-time мини-SSG (`scripts/build-content/*`), сервером
 *   (`routes/replay-page.ts` — canonical + EN-first OG-копии, T-SEO-6) и клиентом
 *   (`lib/i18n`, рейл-контрол переключателя языка T-I18N-2).
 * NOT IN SCOPE: тексты контент-страниц (данные в `content/<lang>/*.md`, шаг 4
 *   SEO-плана / копирайтинг); рантайм-движок SPA-локализации — он живёт в
 *   `packages/client/src/lib/i18n/` (ADR-0021 D-1), здесь ТОЛЬКО конфиг-данные
 *   языков; визуальный рендер флага (SVG-контрол рейла, T-I18N-2 — здесь лишь код
 *   страны-флага как данные); ключевые слова (Ahrefs/SEMrush — owner, шаг 4).
 *
 * Cross-references: ADR-0020 (`docs/decisions/ADR-0020-seo-per-route-unified-product/README.md`)
 *   Решения 1/2/4/5; ADR-0021 (`docs/decisions/ADR-0021-i18n-global-language/README.md`)
 *   D-1/D-3/D-5a/D-6/D-8 — два списка языков, гео-дефолт, hreflang-теги, SVG-флаги;
 *   `docs/research/seo/seo-strategy.md` §1.4/§1.6/§1.8;
 *   `docs/research/seo/owner-inputs.md` §i18n (авторитетные значения 2026-06-18);
 *   `PUBLIC_BASE_URL` в `./balance.ts` (canonical host, owner-decision 2026-05-24).
 */
/**
 * Публичное имя бренда. Используется в `<title>`-суффиксе контент-страниц, OG
 * `og:site_name`, schema `Organization`/`WebApplication`/`VideoGame` → `name`.
 *
 * **Owner-input 2026-06-16:** `Color Lines 98 Online` (verbatim,
 * `docs/research/seo/owner-inputs.md`). Закрывает open-Q O-2 ADR-0020.
 */
export const BRAND_NAME = 'Color Lines 98 Online';
/**
 * Голый домен (host без схемы) — для schema.org `Organization.url` человеко-
 * читаемых упоминаний и as-display. **Полный canonical base** (`https://…`)
 * берётся из `PUBLIC_BASE_URL` (`./balance.ts`), чтобы не дублировать схему/host
 * в двух местах — `DOMAIN` это display-форма того же хоста.
 *
 * **Owner-input 2026-06-16:** `colorlines98.online`.
 */
export const DOMAIN = 'colorlines98.online';
/**
 * Дефолтный язык контента (ISO 639-1). Корень `/` редиректит на `/<DEFAULT>/`;
 * `x-default` hreflang указывает на этот язык. **Owner-приоритет EU/USA →
 * English-first** (owner-input 2026-06-16; ADR-0020 Решение 2). Меняется ОДНОЙ
 * константой + редирект-правилом — URL-каркас (подпапки) это выдерживает
 * (ADR-0020 Revisit-trigger 5).
 */
export const DEFAULT_CONTENT_LANG = 'en';
/**
 * ВСЕ языки UI-переключателя (рейл-контрол, T-I18N-2) — 15 кодов. Это ВИДИМЫЙ список.
 *
 * Порядок = ПРИОРИТЕТ ЦЕЛЕВЫХ РЫНКОВ (owner-decision 2026-06-27): ядро (en/ru) → крупные
 * emerging + LatAm (es/pt/id/tr/vi/th) → Восточная Азия (ja/ko/zh-CN) → Западная Европа
 * (de/fr/it) → zh-TW. ТЕКУЩИЙ язык всегда сверху отдельным ТРИГГЕРОМ переключателя
 * (lib/nav-lang) — поэтому список идёт по рынкам, а активный язык виден наверху как триггер.
 * На SEO порядок НЕ влияет (за многоязычность отвечают hreflang, порядко-независимо) — это
 * чисто UX-приоритет (research 2026-06-27: smartling/digital.gov — порядок = UX, не ранкинг).
 *
 * Коды с дефисом/регистром (`zh-CN`/`zh-TW`) — варианты китайского по письменности
 * (Simplified/Traditional, ADR-0021 D-5a → hreflang `zh-Hans`/`zh-Hant`, см. `HREFLANG_TAG`).
 * Инвариант: `CONTENT_LANGS ⊆ UI_LANGS` (проверяется тип-гардом ниже).
 */
export const UI_LANGS = [
    'en',
    'ru',
    'es',
    'pt',
    'id',
    'tr',
    'vi',
    'th',
    'ja',
    'ko',
    'zh-CN',
    'de',
    'fr',
    'it',
    'zh-TW',
];
/**
 * Языки, для которых мини-SSG генерирует ИНДЕКСИРУЕМЫЕ страницы (ISO 639-1).
 * Скрипт итерирует `CONTENT_LANGS × pages` и проставляет hreflang симметрично +
 * `x-default`. **`en` + `ru` (owner-directive 2026-06-16, постоянная: контент-
 * страницы ВСЕГДА на обоих языках сразу — `docs/research/seo/owner-inputs.md`).**
 * `de` + `es` добавлены 2026-06-23 — их SEO-переводы готовы (`content/de/` +
 * `content/es/`, своды `docs/seo/SEO_text_{DE,ES}_for_translation.md`).
 * `fr/it/pt/ja/ko/vi/zh-CN/zh-TW` добавлены 2026-06-23 — их SEO-переводы готовы
 * (`content/<L>/` + `content/i18n/<L>.json`, своды `docs/seo/SEO_text_<L>_for_translation.md`).
 * После этого `CONTENT_LANGS == UI_LANGS` (все индексируемы — «языков-заглушек»
 * больше НЕТ; редирект корня → любой не-en индексируемый язык, см. resolve-locale).
 * Дефолт/canonical/x-default остаётся `en` (SEO-приоритет EU/USA + hreflang).
 * Следующий язык добавляется в список КОГДА его перевод готов, не раньше (ADR-0020
 * Риск 5 / ADR-0021 D-6: не плодим пустые/частичные языки — дубль-контент-риск).
 * Добавить язык = добавить код сюда + папку `content/<lang>/` +
 * `content/i18n/<lang>.json` (код УЖЕ должен быть в `UI_LANGS`).
 *
 * **tr/id/th добавлены 2026-06-25 (owner-directive «полноценные языки, не заглушки»).**
 * На момент регистрации (задача L0a) их `content/i18n/<lang>.json` = временная копия
 * `en.json` (одинаковый набор ключей, английский fallback — чтобы loadCatalog не
 * вернул null и SPA-чейн не падал), а SEO-страниц `content/tr|id|th/*.md` ЕЩЁ НЕТ —
 * их создаёт задача L0b, перевод каталога — L1. Поэтому мини-SSG (build-content)
 * временно бросает на отсутствии `content/tr|id|th/*.md`, пока L0b не добавит страницы
 * (известно оркестратору; регистрация откатываться НЕ должна — owner: подключить
 * полноценно). SPA-рантайм (рейл-переключатель, resolve-locale) с этой регистрацией
 * работает уже сейчас.
 *
 * `DEFAULT_CONTENT_LANG` обязан присутствовать в этом списке (инвариант).
 * Это ПОДМНОЖЕСТВО `UI_LANGS` (ADR-0021 D-1; гард ниже).
 */
export const CONTENT_LANGS = [
    'en',
    'ru',
    'de',
    'es',
    'fr',
    'it',
    'pt',
    'ja',
    'ko',
    'vi',
    'zh-CN',
    'zh-TW',
    'tr',
    'id',
    'th',
];
/**
 * Тип-гард инварианта `CONTENT_LANGS ⊆ UI_LANGS` (ADR-0021 D-1): каждый
 * контент-язык обязан присутствовать в UI-списке. Если в `CONTENT_LANGS` попадёт
 * код не из `UI_LANGS`, TS упадёт здесь на компиляции — отдельный тест не нужен.
 * Чисто compile-time (помечен `void`, чтобы не остаться неиспользуемым).
 */
const _contentSubsetOfUi = CONTENT_LANGS;
void _contentSubsetOfUi;
/**
 * Нативные названия языков (эндонимы) для пункта переключателя на рейле (флаг
 * слева + имя справа, ADR-0021 D-7) и для language-switcher контент-страниц.
 * Тип `Record<UiLang, string>` — гард полноты: пропуск языка → ошибка компиляции,
 * отдельный тест не нужен (ADR-0021 D-1). Имена — owner-канон (owner-inputs §i18n).
 */
export const LANG_LABELS = {
    ru: 'Русский',
    en: 'English',
    de: 'Deutsch',
    es: 'Español',
    pt: 'Português',
    it: 'Italiano',
    fr: 'Français',
    'zh-CN': '简体中文',
    ja: '日本語',
    ko: '한국어',
    'zh-TW': '繁體中文',
    vi: 'Tiếng Việt',
    // tr/id/th — эндонимы (нативные самоназвания), 2026-06-25.
    tr: 'Türkçe',
    id: 'Bahasa Indonesia',
    th: 'ไทย',
};
/**
 * Код страны-флага (ISO-3166-1 alpha-2, нижний регистр) для рендера флага SVG в
 * переключателе (ADR-0021 D-8). **НЕ unicode flag-emoji** — Windows/Chrome не
 * рендерят regional-indicator как флаг (показывают буквы); визуальный SVG строит
 * рейл-контрол (T-I18N-2), здесь — только данные. Флаг ≠ язык (известная ловушка):
 * en→us (owner п.13 2026-06-21: флаг США для English вместо британского), pt→br
 * (объём аудитории), zh-CN→cn, zh-TW→tw (owner задал; политически чувствительно —
 * Revisit-2 ADR-0021: смена на письменность-метку 简/繁 = одна правка config). Язык/
 * регион/SEO (HREFLANG_TAG/COUNTRY_TO_LANG/LANG_LABELS) от смены флага НЕ меняются.
 * Тип `Record<UiLang>` — гард полноты.
 */
export const LANG_FLAG = {
    ru: 'ru',
    en: 'us',
    de: 'de',
    es: 'es',
    pt: 'br',
    it: 'it',
    fr: 'fr',
    'zh-CN': 'cn',
    ja: 'jp',
    ko: 'kr',
    'zh-TW': 'tw',
    vi: 'vn',
    // tr/id/th — ISO-3166 коды стран для SVG-флага (2026-06-25). Флаг = страна языка
    // (Турция/Индонезия/Таиланд); SVG строит lib/i18n/flags по этому коду.
    tr: 'tr',
    id: 'id',
    th: 'th',
};
/**
 * Направление письма каждого языка для атрибута `dir`/`<html dir>` (ADR-0021 D-1).
 * Все 12 текущих языков — `ltr`; карта заведена задел-на-будущее (добавление RTL
 * языка, напр. ar/he, потребует лишь записи `'rtl'` здесь). Тип `Record<UiLang>` —
 * гард полноты.
 */
export const LANG_DIR = {
    ru: 'ltr',
    en: 'ltr',
    de: 'ltr',
    es: 'ltr',
    pt: 'ltr',
    it: 'ltr',
    fr: 'ltr',
    'zh-CN': 'ltr',
    ja: 'ltr',
    ko: 'ltr',
    'zh-TW': 'ltr',
    vi: 'ltr',
    // tr/id/th — все LTR (2026-06-25): турецкий (латиница), индонезийский (латиница),
    // тайский (тайское письмо, но направление LTR).
    tr: 'ltr',
    id: 'ltr',
    th: 'ltr',
};
/**
 * BCP-47 тег для атрибута `hreflang` в `<link>`/sitemap (ADR-0021 D-5a). Для
 * вариантов китайского Google точнее работает по ПИСЬМЕННОСТИ, чем по региону:
 * `zh-CN`→`zh-Hans` (Simplified), `zh-TW`→`zh-Hant` (Traditional). Остальные коды
 * = сам код (`ru`→`ru`, `en`→`en`, …). Карта готова для всех 12; применяется по
 * мере входа языка в `CONTENT_LANGS` (заглушки в hreflang НЕ попадают — D-5a).
 * Тип `Record<UiLang>` — гард полноты.
 */
export const HREFLANG_TAG = {
    ru: 'ru',
    en: 'en',
    de: 'de',
    es: 'es',
    pt: 'pt',
    it: 'it',
    fr: 'fr',
    'zh-CN': 'zh-Hans',
    ja: 'ja',
    ko: 'ko',
    'zh-TW': 'zh-Hant',
    vi: 'vi',
    // tr/id/th — hreflang-теги = сам ISO 639-1 код (нет вариантов письменности/региона),
    // 2026-06-25.
    tr: 'tr',
    id: 'id',
    th: 'th',
};
/**
 * Таблица ISO-3166-1 alpha-2 страна → язык UI для гео-дефолта первого захода
 * (ADR-0021 D-3). Покрывает очевидные соответствия; страны ВНЕ таблицы → фолбэк
 * `en` (этот фолбэк делает РЕЗОЛВЕР T-I18N-3, не таблица — здесь только маппинг).
 * Значения — из `UI_LANGS` (TS гарантирует). GDPR: сырой IP не хранится —
 * резолвер сводит к коду страны → языку и выбрасывает (ADR-0021 §Consequences).
 */
export const COUNTRY_TO_LANG = {
    // Русский
    RU: 'ru',
    BY: 'ru',
    KZ: 'ru',
    // English
    GB: 'en',
    US: 'en',
    AU: 'en',
    CA: 'en',
    IE: 'en',
    NZ: 'en',
    // Deutsch
    DE: 'de',
    AT: 'de',
    CH: 'de',
    // Español
    ES: 'es',
    MX: 'es',
    AR: 'es',
    CO: 'es',
    CL: 'es',
    // Português
    PT: 'pt',
    BR: 'pt',
    // Italiano
    IT: 'it',
    // Français
    FR: 'fr',
    BE: 'fr',
    // 简体中文 (Simplified)
    CN: 'zh-CN',
    SG: 'zh-CN',
    // 日本語
    JP: 'ja',
    // 한국어
    KR: 'ko',
    // 繁體中文 (Traditional)
    TW: 'zh-TW',
    HK: 'zh-TW',
    // Tiếng Việt
    VN: 'vi',
    // Türkçe
    TR: 'tr',
    // Bahasa Indonesia
    ID: 'id',
    // ไทย (Thai)
    TH: 'th',
};
/**
 * Путь к ОСНОВНОМУ логотипу (SVG, scalable, ~1.5 КБ — лёгкий для CWV/бандла,
 * owner-input 2026-06-16). Относительно корня сайта после деплоя (логотип
 * копируется в выходную статику build-content'ом). PNG-запас
 * (`/logo/colorlines98-logo-concept.png`) — для OG-изображения при нужде.
 *
 * Логотип размещается в ЛЕВОМ ВЕРХНЕМ углу контент-страницы и КЛИКАБЕЛЕН → ведёт
 * на лендинг (домашнюю страницу `/<lang>/`). Owner-input 2026-06-16.
 */
export const LOGO_SVG_PATH = '/logo/colorlines98-logo-concept.svg';
export const LOGO_PNG_PATH = '/logo/colorlines98-logo-concept.png';
//# sourceMappingURL=seo.js.map